export type RetailVertical =
  | 'Accessories & Jewelry'
  | 'Apparel Contemporary Fashion'
  | 'Apparel - Fast Fashion'
  | 'Apparel - Kids'
  | 'Apparel - Luxury Fashion'
  | 'Apparel - Western'
  | 'Beauty & Personal Care'
  | 'Electronics'
  | 'Fabric, Quilt & Yarn'
  | 'Footwear'
  | 'Home & Garden'
  | 'Kitchen'
  | 'Other Retail'
  | 'Sports & Outdoor'
  | 'Toys & Hobby'

export type VerticalBenchmarks = {
  salesYoYPct: number
  imuPct: number
  markdownPct: number
  grossMarginPct: number
  cashMarginPct: number
  inventoryTurn: number
  freshnessPct: number
}

export type HealthMetricResult = {
  label: string
  score: number
  weight: number
  actual: number
  benchmark: number
  // top-10% bar the metric earns full points at (resolveExcellentBar)
  excellentBar: number
  lowerIsBetter: boolean
}

export type HealthScoreResult = {
  totalScore: number
  metrics: HealthMetricResult[]
}

export const VERTICAL_BENCHMARKS: Record<RetailVertical, VerticalBenchmarks> = {
  'Accessories & Jewelry': { salesYoYPct: 10, imuPct: 67, markdownPct: 10, grossMarginPct: 63.7, cashMarginPct: 60, inventoryTurn: 3.0, freshnessPct: 53 },
  'Apparel Contemporary Fashion': { salesYoYPct: 9, imuPct: 55, markdownPct: 13, grossMarginPct: 49.2, cashMarginPct: 50, inventoryTurn: 3.3, freshnessPct: 58 },
  'Apparel - Fast Fashion': { salesYoYPct: 12, imuPct: 65, markdownPct: 20, grossMarginPct: 58.0, cashMarginPct: 54, inventoryTurn: 4.2, freshnessPct: 72 },
  'Apparel - Kids': { salesYoYPct: 8, imuPct: 60, markdownPct: 18, grossMarginPct: 52.8, cashMarginPct: 51, inventoryTurn: 4.0, freshnessPct: 70 },
  'Apparel - Luxury Fashion': { salesYoYPct: 7, imuPct: 60, markdownPct: 14, grossMarginPct: 54.4, cashMarginPct: 53, inventoryTurn: 3.1, freshnessPct: 52 },
  'Apparel - Western': { salesYoYPct: 7, imuPct: 56, markdownPct: 14, grossMarginPct: 49.8, cashMarginPct: 52, inventoryTurn: 2.7, freshnessPct: 58 },
  'Beauty & Personal Care': { salesYoYPct: 12, imuPct: 63, markdownPct: 7, grossMarginPct: 60.4, cashMarginPct: 59, inventoryTurn: 4.2, freshnessPct: 72 },
  Electronics: { salesYoYPct: 4, imuPct: 37, markdownPct: 8, grossMarginPct: 32.0, cashMarginPct: 34, inventoryTurn: 6.0, freshnessPct: 68 },
  'Fabric, Quilt & Yarn': { salesYoYPct: 5, imuPct: 58, markdownPct: 6, grossMarginPct: 55.5, cashMarginPct: 55, inventoryTurn: 2.8, freshnessPct: 45 },
  Footwear: { salesYoYPct: 6, imuPct: 56, markdownPct: 13, grossMarginPct: 50.3, cashMarginPct: 52, inventoryTurn: 2.7, freshnessPct: 58 },
  'Home & Garden': { salesYoYPct: 5, imuPct: 53, markdownPct: 12, grossMarginPct: 47.4, cashMarginPct: 47, inventoryTurn: 3.0, freshnessPct: 48 },
  Kitchen: { salesYoYPct: 6, imuPct: 55, markdownPct: 10, grossMarginPct: 50.5, cashMarginPct: 51, inventoryTurn: 3.2, freshnessPct: 52 },
  'Other Retail': { salesYoYPct: 6, imuPct: 57, markdownPct: 10, grossMarginPct: 52.7, cashMarginPct: 52, inventoryTurn: 3.5, freshnessPct: 58 },
  'Sports & Outdoor': { salesYoYPct: 6, imuPct: 53, markdownPct: 14, grossMarginPct: 46.4, cashMarginPct: 46, inventoryTurn: 3.2, freshnessPct: 58 },
  'Toys & Hobby': { salesYoYPct: 7, imuPct: 56, markdownPct: 16, grossMarginPct: 49.0, cashMarginPct: 48, inventoryTurn: 3.1, freshnessPct: 55 },
}

export const DEFAULT_VERTICAL: RetailVertical = 'Apparel Contemporary Fashion'

export const HEALTH_WEIGHTS = {
  cashMargin: 30,
  salesGrowth: 25,
  markdowns: 20,
  turn: 15,
  freshness: 10,
} as const

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

// Freshness = receipts at retail over the last 2 closed months ÷ current inventory,
// capped at 100%. One standard window for every vertical.
export const FRESHNESS_WINDOW_DAYS = 60
export const FRESHNESS_CAP_PCT = 100

// Share of a metric's points earned at the top-25% benchmark; the rest is earned between
// the benchmark and the top-10% bar. 100 = top 10% on every metric; top 25% = 85.
const SHARE_AT_BENCHMARK = 0.85

// Relative shortfall vs benchmark: positive = worse, negative = better.
// Higher is better: 1 − actual/benchmark. Lower is better (markdowns): 1 − benchmark/actual,
// so 13.7% against a 9.6% benchmark achieves 70% of it rather than being "43% short".
// Denominators are floored at 1 unit so a zero or negative benchmark (e.g. sales growth in
// a down year) can't flip the sign or divide by zero.
export function computeVariance(actual: number, benchmark: number, lowerIsBetter: boolean): number {
  return lowerIsBetter
    ? (actual - benchmark) / Math.max(Math.abs(actual), 1)
    : (benchmark - actual) / Math.max(Math.abs(benchmark), 1)
}

// The top-10% bar: the peer bar (P90; P10 for markdowns) when it lies beyond the benchmark,
// else the benchmark ± 15% x max(|benchmark|, 1); never past the metric's ceiling.
// Mirrored in GetHealthScoreV2.sql (*Pts) and IndieInsightsController::healthTone.
export function resolveExcellentBar(
  benchmark: number,
  lowerIsBetter: boolean,
  peerBar?: number | null,
  ceiling?: number,
): number {
  const beyond = peerBar != null && (lowerIsBetter ? peerBar < benchmark : peerBar > benchmark)
  const step = 0.15 * Math.max(Math.abs(benchmark), 1)
  const bar = beyond ? peerBar! : lowerIsBetter ? benchmark - step : benchmark + step
  return ceiling != null ? Math.min(bar, ceiling) : bar
}

// Share (0–1) of a metric's points: below the benchmark, 0.85 × (1 − shortfall), with turn
// losing it half as fast (it swings with seasonal stock); from the benchmark to the top-10%
// bar, 0.85 rising linearly to 1. Mirrored in GetHealthScoreV2.sql (the *Pts columns).
function metricPointShare(label: string, actual: number, benchmark: number, lowerIsBetter: boolean, excellentBar: number): number {
  const variance = computeVariance(actual, benchmark, lowerIsBetter)
  const shortfallForZero = label === 'Turnover' ? 2 : 1
  if (variance > 0) return SHARE_AT_BENCHMARK * (1 - clamp(variance / shortfallForZero, 0, 1))
  const span = lowerIsBetter ? benchmark - excellentBar : excellentBar - benchmark
  const progress = span > 0 ? (lowerIsBetter ? benchmark - actual : actual - benchmark) / span : 1
  return SHARE_AT_BENCHMARK + (1 - SHARE_AT_BENCHMARK) * clamp(progress, 0, 1)
}

// Position (0–100) of a metric on its directional bar = its share of points, so the
// benchmark tick sits at BAR_BENCHMARK_POS and the top-10% bar at the far end. Returned
// "better = right"; flip it for lower-is-better bars.
export const BAR_BENCHMARK_POS = SHARE_AT_BENCHMARK * 100

export function metricBarPosition(metric: HealthMetricResult): number {
  return metricPointShare(metric.label, metric.actual, metric.benchmark, metric.lowerIsBetter, metric.excellentBar) * 100
}

// null = no data (e.g. no prior-year sales, no current inventory): the metric is left out of
// the score, as in GetHealthScoreV2.sql.
export type HealthScoreInputs = {
  grossMarginPct: number | null
  salesYoYPct: number | null
  markdownPct: number | null
  inventoryTurn: number | null
  freshnessPct: number | null
}

// The five scored benchmarks (P75; P25 for markdowns) from /api/indie-insights/health, or
// the hand-set VERTICAL_BENCHMARKS table when that feed is unavailable.
export type ScoringBenchmarks = Pick<VerticalBenchmarks, 'grossMarginPct' | 'salesYoYPct' | 'markdownPct' | 'inventoryTurn' | 'freshnessPct'>

export function computeHealthScore(
  inputs: HealthScoreInputs,
  benchmarks: ScoringBenchmarks = VERTICAL_BENCHMARKS[DEFAULT_VERTICAL],
  // peer top-10% bars; missing ones are derived (resolveExcellentBar)
  peerBars: Partial<ScoringBenchmarks> = {},
): HealthScoreResult {
  const specs: Array<[string, keyof ScoringBenchmarks, number, boolean, number?]> = [
    ['Gross Margin', 'grossMarginPct', HEALTH_WEIGHTS.cashMargin, false],
    ['Sales Growth', 'salesYoYPct', HEALTH_WEIGHTS.salesGrowth, false],
    ['Markdowns', 'markdownPct', HEALTH_WEIGHTS.markdowns, true],
    ['Turnover', 'inventoryTurn', HEALTH_WEIGHTS.turn, false],
    ['Freshness Factor', 'freshnessPct', HEALTH_WEIGHTS.freshness, false, FRESHNESS_CAP_PCT],
  ]
  const metrics: HealthMetricResult[] = []
  for (const [label, key, weight, lowerIsBetter, ceiling] of specs) {
    const actual = inputs[key]
    if (actual == null || !Number.isFinite(actual)) continue
    const benchmark = benchmarks[key]
    const excellentBar = resolveExcellentBar(benchmark, lowerIsBetter, peerBars[key], ceiling)
    const share = metricPointShare(label, actual, benchmark, lowerIsBetter, excellentBar)
    metrics.push({ label, score: Math.round(share * weight * 10) / 10, weight, actual, benchmark, excellentBar, lowerIsBetter })
  }
  return { totalScore: Math.round(metrics.reduce((sum, metric) => sum + metric.score, 0)), metrics }
}

export function healthMetricDisplayLabel(label: string): string {
  switch (label) {
    case 'Turnover': return 'Annual Turn'
    case 'Freshness Factor': return 'Inventory Freshness'
    default: return label
  }
}

// Tier, compared at display precision (turn 2 dp, others 1 dp): Excellent = meets the
// benchmark and beats the top-10% bar (reaching a capped bar counts); Good = meets the
// benchmark; Average / Fair / Poor by share of points vs the benchmark's (≥ 85%, ≥ 65%).
// Mirrored in IndieInsightsController::healthTone.
export function metricScoreTone(metric: HealthMetricResult): 'excellent' | 'veryGood' | 'average' | 'belowAverage' | 'critical' {
  const precision = metric.label === 'Turnover' ? 100 : 10
  const shown = Math.round(metric.actual * precision) / precision
  const bar = metric.excellentBar
  const atCeiling = metric.label === 'Freshness Factor' && bar >= FRESHNESS_CAP_PCT && shown >= FRESHNESS_CAP_PCT
  const meets = metric.lowerIsBetter ? shown <= metric.benchmark : shown >= metric.benchmark
  const beats = metric.lowerIsBetter ? shown < bar : shown > bar || atCeiling
  const pct = metric.weight > 0 ? metric.score / metric.weight / SHARE_AT_BENCHMARK : 0
  if (meets && beats) return 'excellent'
  if (meets) return 'veryGood'
  if (pct >= 0.85) return 'average'
  if (pct >= 0.65) return 'belowAverage'
  return 'critical'
}

// Percentile (0–100) of `score` within a score distribution given as evenly spaced
// quantiles (0th … 100th), interpolating between neighbours.
export function percentileFromQuantiles(score: number, quantiles: number[] | null | undefined): number | null {
  if (!quantiles || quantiles.length < 2) return null
  const step = 100 / (quantiles.length - 1)
  if (score < quantiles[0]) return 0
  if (score >= quantiles[quantiles.length - 1]) return 100
  for (let i = 0; i < quantiles.length - 1; i++) {
    const lo = quantiles[i]
    const hi = quantiles[i + 1]
    if (score >= lo && score < hi) return Math.round(step * i + (step * (score - lo)) / (hi - lo))
  }
  return null
}

// Raw vertical name (e.g. "Contemporary Fashion", "Toys / Hobby") -> hand-set default row.
// Mirror of VerticalDefaultKey in IndieInsights/GetHealthScoreV2.sql: exact key, known
// aliases, normalized-equality match; null when nothing matches.
const VERTICAL_ALIASES: Record<string, RetailVertical> = {
  'Sports and Outdoor': 'Sports & Outdoor',
  'Outdoor/Sporting Goods': 'Sports & Outdoor',
  Outdoor: 'Sports & Outdoor',
  'Sporting Goods': 'Sports & Outdoor',
  'Contemporary Fashion': 'Apparel Contemporary Fashion',
  'Fast Fashion': 'Apparel - Fast Fashion',
  Kids: 'Apparel - Kids',
  'Kids / Maternity': 'Apparel - Kids',
  'Luxury Fashion': 'Apparel - Luxury Fashion',
  Luxury: 'Apparel - Luxury Fashion',
  Western: 'Apparel - Western',
  'Western Wear': 'Apparel - Western',
  'Accessories and Jewelry': 'Accessories & Jewelry',
  'Jewelry Fashion': 'Accessories & Jewelry',
  'Beauty and Personal Care': 'Beauty & Personal Care',
  'Wellness & Beauty': 'Beauty & Personal Care',
  'Home and Garden': 'Home & Garden',
  'Toys and Hobby': 'Toys & Hobby',
  'Toys / Hobby': 'Toys & Hobby',
  'Fabric Quilt and Yarn': 'Fabric, Quilt & Yarn',
  'Quilt / Fabric': 'Fabric, Quilt & Yarn',
  Yarn: 'Fabric, Quilt & Yarn',
}

const normalizeVerticalName = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '')

export function defaultVerticalKey(raw: unknown): RetailVertical | null {
  if (typeof raw !== 'string' || !raw.trim()) return null
  const name = raw.trim()
  if (name in VERTICAL_BENCHMARKS) return name as RetailVertical
  if (VERTICAL_ALIASES[name]) return VERTICAL_ALIASES[name]
  const normalized = normalizeVerticalName(name)
  return (Object.keys(VERTICAL_BENCHMARKS) as RetailVertical[]).find((key) => normalizeVerticalName(key) === normalized) ?? null
}

// Fallback chain for the hand-set defaults: the peer feed's vertical, then the client
// record's vertical, then DEFAULT_VERTICAL.
export function resolveClientVertical(client: any, feedVertical?: unknown): RetailVertical {
  return defaultVerticalKey(feedVertical)
    ?? defaultVerticalKey(client?.shopifyClient?.vertical || client?.clientExtra?.vertical || client?.client_extra?.vertical)
    ?? DEFAULT_VERTICAL
}